import { useCallback } from 'react';
import { getAnalyticsWebClient, sendAnalyticsEvent } from '../analytics';
import type {
  ScreenEventPayload,
  TrackEventPayload,
  UIEventPayload,
  OperationalEventPayload,
} from '@atlassiansox/analytics-web-client';
import { useOneTrustContext } from '@app/onetrust';

/**
 * Custom hook for sending analytics events
 * Provides convenient methods to send different types of analytics events within React components
 */
export const useAnalyticsEvent = () => {
  const { analyticsOptOut } = useOneTrustContext();

  const sendScreenEvent = useCallback(
    (event: ScreenEventPayload) => {
      if (analyticsOptOut) {
        return;
      }
      const client = getAnalyticsWebClient();
      sendAnalyticsEvent(client, 'screen', event);
    },
    [analyticsOptOut],
  );

  const sendTrackEvent = useCallback(
    (event: TrackEventPayload) => {
      if (analyticsOptOut) {
        return;
      }
      const client = getAnalyticsWebClient();
      sendAnalyticsEvent(client, 'track', event);
    },
    [analyticsOptOut],
  );

  const sendUIEvent = useCallback(
    (event: UIEventPayload) => {
      if (analyticsOptOut) {
        return;
      }
      const client = getAnalyticsWebClient();
      sendAnalyticsEvent(client, 'ui', event);
    },
    [analyticsOptOut],
  );

  const sendOperationalEvent = useCallback(
    (event: OperationalEventPayload) => {
      if (analyticsOptOut) {
        return;
      }
      const client = getAnalyticsWebClient();
      sendAnalyticsEvent(client, 'operational', event);
    },
    [analyticsOptOut],
  );

  return {
    sendScreenEvent,
    sendTrackEvent,
    sendUIEvent,
    sendOperationalEvent,
  };
};
